import React, {useEffect, useState} from 'react';
import {FormattedHTMLMessage, useIntl} from 'react-intl';
import {
	trackDeclineModalCancelled,
	trackDeclineModalConfirmed,
	trackDeclineModalOpened,
} from '../../../tracking/amplitude/timeApprovalEvents';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import Styled from 'styled-components';
import GenericModal from '../generic_modal';
import {CSS_CONSTANTS} from '../../../css_variables';

export const REJECTION_MODAL_VARIANT = {
	REJECT: 'reject',
	DECLINE: 'decline',
	REVERSE: 'reverse',
};

const VARIANT_STRINGS = {
	[REJECTION_MODAL_VARIANT.REJECT]: {
		header: 'time_approval.reject_submission',
		body: 'time_approval.reject_for_message',
		description: 'time_approval.write_message_description',
		button: 'time_approval.reject_and_send',
		timeOffBody: 'time_off_approval.reject_for_message',
		timeOffDescription: 'time_off_approval.write_message_description',
	},
	[REJECTION_MODAL_VARIANT.DECLINE]: {
		header: 'time_approval.decline_submission',
		body: 'time_approval.decline_for_message',
		description: 'time_approval.write_decline_message_description',
		button: 'time_approval.decline_and_send',
		timeOffBody: 'time_off_approval.decline_for_message',
		timeOffDescription: 'time_off_approval.write_decline_message_description',
	},
	[REJECTION_MODAL_VARIANT.REVERSE]: {
		header: 'time_approval.reverse_approval',
		body: 'time_approval.reverse_approval_for_message',
		description: 'time_approval.write_reverse_message_description',
		button: 'time_approval.reverse_and_send',
		timeOffBody: 'time_off_approval.reverse_approval_for_message',
		timeOffDescription: 'time_off_approval.write_reverse_message_description',
	},
};

export const RejectSubmissionModal = ({
	rejectForName,
	startDate,
	endDate,
	isTimeOffOnly,
	variant = REJECTION_MODAL_VARIANT.REJECT,
	onReject = () => false,
	onCancel,
	closeModal,
}) => {
	const intl = useIntl();
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');

	const requiresMessage = variant === REJECTION_MODAL_VARIANT.DECLINE || variant === REJECTION_MODAL_VARIANT.REVERSE;

	// Empty deps on purpose: once per mount, and the modal is mounted fresh per invocation, so
	// this is once per opening. `variant` is fixed for the life of a given modal instance.
	useEffect(() => {
		trackDeclineModalOpened(variant);
	}, []);

	// Wrap closeModal so any non-confirm dismissal (Cancel button, X icon) resets the caller's loading state
	const handleCloseModal = () => {
		trackDeclineModalCancelled(Boolean(message.trim()));
		onCancel?.();
		closeModal();
	};

	const handleConfirm = () => {
		const trimmedMessage = message.trim();
		if (requiresMessage && !trimmedMessage) {
			setError(intl.formatMessage({id: 'common.required_field'}));
			return;
		}
		// Tracked after the guard, never before: a blocked click is not a confirm, and firing
		// above would record hasReason: false for the variants where a reason is mandatory —
		// a value that cannot describe a real confirm.
		trackDeclineModalConfirmed(Boolean(trimmedMessage), variant);
		onReject(trimmedMessage);
		closeModal();
	};

	const strings = VARIANT_STRINGS[variant];
	const bodyId = (isTimeOffOnly && strings.timeOffBody) || strings.body;
	const descriptionId = (isTimeOffOnly && strings.timeOffDescription) || strings.description;

	const content = (
		<div className="context" style={{fontSize: '13px', width: '384px'}}>
			<div style={{marginBottom: '24px', color: '#535353'}}>
				<FormattedHTMLMessage
					id={bodyId}
					values={{
						startDate: startDate.format('D MMM'),
						endDate: endDate.format('D MMM'),
						name: rejectForName,
					}}
				/>
			</div>
			<div style={{marginBottom: '16px', color: '#535353'}}>
				<FormattedHTMLMessage id={descriptionId} values={{name: rejectForName}} />
			</div>
			<div>
				<TextArea
					value={message}
					rows={3}
					onChange={e => {
						setMessage(e.target.value);
						if (error) setError('');
					}}
					placeholder={`${intl.formatMessage({id: 'time_approval.message_to'})} ${rejectForName}...`}
				/>
				{error && <ErrorText>{error}</ErrorText>}
			</div>
		</div>
	);
	return (
		<GenericModal
			closeModal={handleCloseModal}
			buttons={[
				{
					text: intl.formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: intl.formatMessage({id: strings.button}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					cy: 'reject',
					callback: handleConfirm,
					preventDefaultClose: true,
				},
			]}
			headerText={intl.formatMessage({id: strings.header})}
			content={content}
		/>
	);
};

const TextArea = Styled.textarea`
	position: relative;
	width: 100%;
	color: #535353;
	line-height: 16px;
	font-size: 13px;
	background-color: $color-white;
	-webkit-tap-highlight-color: rgba(0, 0, 0, 0);
	resize: none;
	border-radius: 3px;
	border: solid 1px #e6e6e6;
	padding: 8px;
	outline: none;
	z-index: 99;
	&::placeholder {
		font-size: 13px;
		color: ${CSS_CONSTANTS.v2_text_light_gray};
		opacity: 1;
	}

	&:disabled {
		color: ${CSS_CONSTANTS.v2_text_light_gray};
		background-color: #f3f3f3;
		&:hover {
			border-color: ${CSS_CONSTANTS.v2_border_gray};
		}
	}
	&:hover {
		border-color: ${CSS_CONSTANTS.v2_button_very_light_grey_hovered};
	}
	&:focus {
		border-color: #6e0fea;
		outline: none;
	}
`;

const ErrorText = Styled.span`
	color: ${CSS_CONSTANTS.negative_value};
	font-size: 11px;
	margin-top: 4px;
	display: block;
`;
